import React, { useEffect, useRef, useState } from "react";

import { MdmDeviceDto, MdmObjectDto, TablesDetailsInput, useUpdateTableDetailsForUserMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTablePFSEvent } from "primereact/datatable";
import { Path } from "react-hook-form";
import { exportExcel, findObjectVal, formatDateString } from "utils/utils";
import useProjectContext from "../hooks/useProjectContext";
import { MdmTableName, TableColumn } from "../utils/types";
import { errorToaster, successToaster, userTableDetails } from "../utils/utils";
import TableToolbar from "./TableToolbar";
import TableColumnSelection from "./TableColumnSelection";

type ItemsTableProps<T> = {
  columns: TableColumn<T>[];
  data: T[];
  searchFields?: Path<T>[];
  toolbarBtnClassName?: string;
  toolbarBtnLabel?: string;
  onToolbarBtnClick?: () => void;
  loading?: boolean;
  showToolbar?: boolean;
  showToolbarBtn?: boolean;
  tableName: MdmTableName;
  setFilteredData?: (data: T[]) => void;
};
const ItemsTable = <T,>({
  columns,
  data,
  onToolbarBtnClick = () => {},
  searchFields = [],
  toolbarBtnClassName = "",
  toolbarBtnLabel,
  loading,
  showToolbar = true,
  showToolbarBtn = true,
  tableName,
  setFilteredData,
}: ItemsTableProps<T>) => {
  const { userSettings, project } = useProjectContext();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [filterItems, setFilterItems] = useState<T[]>(data);
  const [excelData, setExcelData] = useState<T[]>([]);
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const dt = useRef<DataTable>(null);
  const tableDetails = userTableDetails(userSettings, tableName);

  const [updateTableDetails, { loading: updateLoading }] = useUpdateTableDetailsForUserMutation({
    onError: errorToaster,
  });

  const customCols =
    tableName === "DevicesByObject"
      ? [...(project?.mdmProjectById.configuration?.deviceConfiguration.customFields ?? [])]
      : [...(project?.mdmProjectById.configuration?.objectConfiguration.customFields ?? [])];

  const mapCols = [
    ...columns,
    ...(customCols?.map(col => ({
      field: `${col?.id}.value`,
      header: col?.title ?? "",
      sortable: true,
      filter: true,
      columnKey: col?.id,
    })) ?? []),
  ] as TableColumn<T>[];

  const formatObjectFields = filterItems.reduce((acc, d) => {
    const customFields =
      tableName === "DevicesByObject"
        ? (d as { mdmDevice: MdmDeviceDto }).mdmDevice?.customFields
        : (d as { mdmObject: MdmObjectDto }).mdmObject?.customFields;
    const objFields = customFields?.reduce(
      (accObj, field) => {
        const newObj = {
          value: field?.value?.value,
          id: field?.key,
          title: customCols.find(col => col?.id === field?.key)?.title,
        };
        return {
          ...accObj,
          [field?.key!]: newObj,
        };
      },
      {}
    );

    acc.push({
      ...d,
      ...objFields,
    });

    return acc;
  }, [] as any[]);

  const filteredColumns = mapCols.filter(col => customColumns.includes(col.header));

  useEffect(() => {
    if(formatObjectFields) {
      setExcelData(formatObjectFields);
      dt.current?.reset();
    }
  }, [customColumns]);

  useEffect(() => {
    const columnList = userTableDetails(userSettings, tableName)?.columnOrders?.map(r => r!.name) ?? [];
    if (userSettings && columnList.length > 0) {
      setCustomColumns(columnList);
    } else {
      setCustomColumns(columns.map(col => col.header));
    }
  }, [userSettings]);

  const exportCSV = () => {
    dt.current?.exportCSV({ selectionOnly: false });
  };

  const handleExportExcel = () => {
    const tableData = excelData.map(row => {
      const newData: any = {};

      filteredColumns.forEach(col => {
        if (col.hideInFile || col.hidden) return;
        newData[col.header] = col.field
          ? formatDateString(findObjectVal(row, col.field)?.toString())
          : col.body;
      });
      return newData;
    });
    exportExcel(tableData, tableName);
  }

  const onPage = (e: DataTablePFSEvent) => {
    const tableDetailsInput: TablesDetailsInput = {
      table: tableName,
      rows: e.rows,
      columnOrders: tableDetails?.columnOrders || [],
    };
    setPage(e.first);
    if (pageLoaded && tableDetails?.rows !== e.rows) {
      updateTableDetails({
      variables: { tableDetails: tableDetailsInput },
      })
    }
    setPageLoaded(true);
  };

  const handleColumnSave = () => {
    updateTableDetails({
      variables: {
        tableDetails: {
          table: tableName,
          rows: tableDetails?.rows || 10,
          columnOrders: customColumns.map((col, i) => ({
            name: col,
            order: i,
          })),
        },
      },
    onCompleted: () => successToaster("Columns saved successfully"),
    });
  };

  return (
    <>
      {showToolbar && (
        <TableToolbar
          searchData={data}
          onSearch={setFilterItems}
          onClickBtn={onToolbarBtnClick}
          searchFields={searchFields}
          classNameBtn={toolbarBtnClassName}
          showBtn={showToolbarBtn}
          labelBtn={toolbarBtnLabel}
          additionalControls={
            <>
              <Button
                type="button"
                icon="pi pi-file"
                className="p-button-outlined p-button-warning"
                title="Export to CSV"
                onClick={exportCSV}
              />
              <Button
                type="button"
                icon="pi pi-file-excel"
                className="p-button-outlined p-button-warning"
                title="Export to Excel"
                onClick={handleExportExcel}
              />
              <TableColumnSelection
                customColumns={customColumns}
                setCustomColumns={setCustomColumns}
                handleColumnSave={handleColumnSave}
                mapCol={mapCols}
                loading={updateLoading}
              />
            </>
          }
        />
      )}
      <DataTable
        ref={dt}
        value={formatObjectFields}
        onValueChange={tableData => {
          setExcelData(tableData);
          setFilteredData?.(tableData);
        }}
        stripedRows
        paginator
        rows={tableDetails?.rows || 10}
        reorderableColumns
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        loading={loading}
        size="small"
        className="overflow-x-auto"
        first={page}
        onPage={onPage}
      >
        {filteredColumns.map(col => (
          <Column key={col.header} {...col} />
        ))}
      </DataTable>
    </>
  );
};

export default ItemsTable;
